/**
 * Bulk placement utilities for warehouse rack positioning
 * Supports linear (row) and mirror (double-row) placement patterns
 */

import {
  RackPosition,
  RackDimensions,
  WarehouseDimensions,
  PlacementConfig,
  PreviewRackPosition,
  RackSummary
} from '../types/warehouseLayout';
import {
  snapToGrid,
  racksOverlap,
  isWithinBounds,
  getRotatedDimensions
} from './positionCalculator';

/**
 * Calculate positions for linear (single row) placement
 * Racks are placed in a line from the anchor position
 */
export function calculateLinearPositions(
  anchor: RackPosition,
  racksToPlace: string[],
  rackDimensionsMap: Map<string, RackDimensions>,
  config: PlacementConfig,
  gridUnit: number
): PreviewRackPosition[] {
  const positions: PreviewRackPosition[] = [];
  const anchorDim = rackDimensionsMap.get(anchor.rackNumber);
  if (!anchorDim) return positions;

  const anchorRotated = getRotatedDimensions(anchorDim, anchor.rotation);

  racksToPlace.forEach((rackNumber, index) => {
    const rackDim = rackDimensionsMap.get(rackNumber);
    if (!rackDim) return;

    const rackRotated = getRotatedDimensions(rackDim, anchor.rotation);

    let x: number;
    let y: number;

    if (config.direction === 'horizontal') {
      // Place racks horizontally (side by side)
      const offset = (index + 1) * (anchorRotated.width + config.rackSpacing);
      x = snapToGrid(anchor.x + offset, gridUnit);
      y = anchor.y;
    } else {
      // Place racks vertically (one after another)
      const offset = (index + 1) * (anchorRotated.depth + config.rackSpacing);
      x = anchor.x;
      y = snapToGrid(anchor.y + offset, gridUnit);
    }

    positions.push({
      rackNumber,
      x,
      y,
      rotation: anchor.rotation,
      isValid: true // Will be validated later
    });
  });

  return positions;
}

/**
 * Calculate positions for mirror (double row) placement
 * Creates two parallel rows with an aisle between them (back-to-back racks)
 */
export function calculateMirrorPositions(
  anchor: RackPosition,
  racksToPlace: string[],
  rackDimensionsMap: Map<string, RackDimensions>,
  config: PlacementConfig,
  gridUnit: number
): PreviewRackPosition[] {
  const positions: PreviewRackPosition[] = [];
  const anchorDim = rackDimensionsMap.get(anchor.rackNumber);
  if (!anchorDim) return positions;

  const anchorRotated = getRotatedDimensions(anchorDim, anchor.rotation);
  const racksPerRow = Math.ceil(racksToPlace.length / 2);

  racksToPlace.forEach((rackNumber, index) => {
    const rackDim = rackDimensionsMap.get(rackNumber);
    if (!rackDim) return;

    const rackRotated = getRotatedDimensions(rackDim, anchor.rotation);
    const rowIndex = index < racksPerRow ? 0 : 1; // First or second row
    const positionInRow = index < racksPerRow ? index : index - racksPerRow;

    let x: number;
    let y: number;
    let rotation: 0 | 90 | 180 | 270;

    if (config.direction === 'horizontal') {
      // Rows extend horizontally, aisles are vertical
      const rowOffset = positionInRow * (anchorRotated.width + config.rackSpacing);
      x = snapToGrid(anchor.x + rowOffset, gridUnit);

      // Calculate Y based on row (with aisle and back-to-back spacing)
      if (rowIndex === 0) {
        y = anchor.y;
        rotation = anchor.rotation;
      } else {
        // Mirror row: place on opposite side of aisle
        const aisleOffset = anchorRotated.depth + config.aisleWidth + rackRotated.depth;
        y = snapToGrid(anchor.y + aisleOffset, gridUnit);
        // Flip rotation for mirror effect (180 degrees difference)
        rotation = ((anchor.rotation + 180) % 360) as 0 | 90 | 180 | 270;
      }
    } else {
      // Rows extend vertically, aisles are horizontal
      const rowOffset = positionInRow * (anchorRotated.depth + config.rackSpacing);
      y = snapToGrid(anchor.y + rowOffset, gridUnit);

      if (rowIndex === 0) {
        x = anchor.x;
        rotation = anchor.rotation;
      } else {
        const aisleOffset = anchorRotated.width + config.aisleWidth + rackRotated.width;
        x = snapToGrid(anchor.x + aisleOffset, gridUnit);
        rotation = ((anchor.rotation + 180) % 360) as 0 | 90 | 180 | 270;
      }
    }

    positions.push({
      rackNumber,
      x,
      y,
      rotation,
      isValid: true
    });
  });

  return positions;
}

/**
 * Calculate positions for grid (rows × columns) placement.
 * Racks are placed in a grid pattern from the anchor position.
 */
export function calculateGridPositions(
  anchor: RackPosition,
  racksToPlace: string[],
  rackDimensionsMap: Map<string, RackDimensions>,
  config: PlacementConfig,
  gridUnit: number
): PreviewRackPosition[] {
  const positions: PreviewRackPosition[] = [];
  const anchorDim = rackDimensionsMap.get(anchor.rackNumber);
  if (!anchorDim) return positions;

  const anchorRotated = getRotatedDimensions(anchorDim, anchor.rotation);
  const cols = Math.max(1, config.columns);

  racksToPlace.forEach((rackNumber, index) => {
    const rackDim = rackDimensionsMap.get(rackNumber);
    if (!rackDim) return;

    const colIndex = index % cols;
    const rowIndex = Math.floor(index / cols);

    let x: number;
    let y: number;
    let rotation: 0 | 90 | 180 | 270 = anchor.rotation;

    if (config.direction === 'horizontal') {
      x = snapToGrid(anchor.x + colIndex * (anchorRotated.width + config.rackSpacing), gridUnit);
      y = snapToGrid(anchor.y + rowIndex * (anchorRotated.depth + config.aisleWidth), gridUnit);
    } else {
      x = snapToGrid(anchor.x + rowIndex * (anchorRotated.width + config.aisleWidth), gridUnit);
      y = snapToGrid(anchor.y + colIndex * (anchorRotated.depth + config.rackSpacing), gridUnit);
    }

    positions.push({
      rackNumber,
      x,
      y,
      rotation,
      isValid: true,
    });
  });

  return positions;
}

/**
 * Validate preview positions against existing racks and warehouse bounds
 */
export function validatePreviewPositions(
  previewPositions: PreviewRackPosition[],
  existingRacks: RackPosition[],
  rackDimensionsMap: Map<string, RackDimensions>,
  warehouseDimensions: WarehouseDimensions,
  margin: number = 0.3
): PreviewRackPosition[] {
  return previewPositions.map(preview => {
    const previewDim = rackDimensionsMap.get(preview.rackNumber);
    if (!previewDim) {
      return { ...preview, isValid: false };
    }

    // Check bounds
    const previewAsRack: RackPosition = {
      rackNumber: preview.rackNumber,
      x: preview.x,
      y: preview.y,
      rotation: preview.rotation
    };

    if (!isWithinBounds(previewAsRack, previewDim, warehouseDimensions)) {
      return { ...preview, isValid: false };
    }

    // Check collision with existing racks
    for (const existing of existingRacks) {
      // Skip if this is the same rack (might be already placed)
      if (existing.rackNumber === preview.rackNumber) continue;

      const existingDim = rackDimensionsMap.get(existing.rackNumber);
      if (!existingDim) continue;

      if (racksOverlap(previewAsRack, previewDim, existing, existingDim, margin)) {
        return { ...preview, isValid: false };
      }
    }

    // Check collision with other preview positions
    for (const other of previewPositions) {
      if (other.rackNumber === preview.rackNumber) continue;

      const otherDim = rackDimensionsMap.get(other.rackNumber);
      if (!otherDim) continue;

      const otherAsRack: RackPosition = {
        rackNumber: other.rackNumber,
        x: other.x,
        y: other.y,
        rotation: other.rotation
      };

      if (racksOverlap(previewAsRack, previewDim, otherAsRack, otherDim, margin)) {
        return { ...preview, isValid: false };
      }
    }

    return { ...preview, isValid: true };
  });
}

/**
 * Calculate preview positions based on placement mode
 */
export function calculatePreviewPositions(
  anchor: RackPosition,
  racksToPlace: string[],
  rackDimensionsMap: Map<string, RackDimensions>,
  existingRacks: RackPosition[],
  warehouseDimensions: WarehouseDimensions,
  config: PlacementConfig,
  gridUnit: number
): PreviewRackPosition[] {
  if (racksToPlace.length === 0) return [];

  let positions: PreviewRackPosition[];

  switch (config.mode) {
    case 'linear':
      positions = calculateLinearPositions(anchor, racksToPlace, rackDimensionsMap, config, gridUnit);
      break;
    case 'mirror':
      positions = calculateMirrorPositions(anchor, racksToPlace, rackDimensionsMap, config, gridUnit);
      break;
    case 'grid':
      positions = calculateGridPositions(anchor, racksToPlace, rackDimensionsMap, config, gridUnit);
      break;
    default:
      positions = [];
  }

  // Validate all positions
  return validatePreviewPositions(
    positions,
    existingRacks,
    rackDimensionsMap,
    warehouseDimensions
  );
}

/**
 * Convert validated preview positions to actual rack positions
 * Only includes valid positions
 */
export function applyPreviewPositions(
  previewPositions: PreviewRackPosition[]
): RackPosition[] {
  return previewPositions
    .filter(p => p.isValid)
    .map(preview => ({
      rackNumber: preview.rackNumber,
      x: preview.x,
      y: preview.y,
      rotation: preview.rotation
    }));
}

/**
 * Move multiple racks while maintaining relative positions
 */
export function moveRacksRelative(
  racks: RackPosition[],
  selectedRacks: Set<string>,
  deltaX: number,
  deltaY: number,
  gridUnit: number
): RackPosition[] {
  return racks.map(rack => {
    if (selectedRacks.has(rack.rackNumber)) {
      return {
        ...rack,
        x: snapToGrid(rack.x + deltaX, gridUnit),
        y: snapToGrid(rack.y + deltaY, gridUnit)
      };
    }
    return rack;
  });
}

/**
 * Rotate multiple selected racks by 90 degrees
 */
export function rotateSelectedRacks(
  racks: RackPosition[],
  selectedRacks: Set<string>
): RackPosition[] {
  return racks.map(rack => {
    if (selectedRacks.has(rack.rackNumber)) {
      return {
        ...rack,
        rotation: ((rack.rotation + 90) % 360) as 0 | 90 | 180 | 270
      };
    }
    return rack;
  });
}

/**
 * Delete multiple selected racks
 */
export function deleteSelectedRacks(
  racks: RackPosition[],
  selectedRacks: Set<string>
): RackPosition[] {
  return racks.filter(rack => !selectedRacks.has(rack.rackNumber));
}

/**
 * Align selected racks to grid
 */
export function alignRacksToGrid(
  racks: RackPosition[],
  selectedRacks: Set<string>,
  gridUnit: number
): RackPosition[] {
  return racks.map(rack => {
    if (selectedRacks.has(rack.rackNumber)) {
      return {
        ...rack,
        x: snapToGrid(rack.x, gridUnit),
        y: snapToGrid(rack.y, gridUnit)
      };
    }
    return rack;
  });
}

/**
 * Calculate total distance (meters) for placing all racks in linear pattern
 * Useful for displaying to user
 */
export function calculateLinearSpan(
  racksToPlace: string[],
  rackDimensionsMap: Map<string, RackDimensions>,
  config: PlacementConfig,
  anchorRotation: 0 | 90 | 180 | 270
): number {
  if (racksToPlace.length === 0) return 0;

  let totalSpan = 0;

  racksToPlace.forEach((rackNumber, index) => {
    const dim = rackDimensionsMap.get(rackNumber);
    if (!dim) return;

    const rotated = getRotatedDimensions(dim, anchorRotation);
    const size = config.direction === 'horizontal' ? rotated.width : rotated.depth;

    if (index < racksToPlace.length - 1) {
      totalSpan += size + config.rackSpacing;
    } else {
      totalSpan += size;
    }
  });

  return totalSpan;
}

/**
 * Calculate dimensions of mirror layout
 * Returns width and depth of the entire double-row layout
 */
export function calculateMirrorDimensions(
  racksToPlace: string[],
  rackDimensionsMap: Map<string, RackDimensions>,
  config: PlacementConfig,
  anchorRotation: 0 | 90 | 180 | 270
): { width: number; depth: number } {
  if (racksToPlace.length === 0) return { width: 0, depth: 0 };

  const racksPerRow = Math.ceil(racksToPlace.length / 2);

  // Calculate row span
  let rowSpan = 0;
  for (let i = 0; i < racksPerRow; i++) {
    const rackNumber = racksToPlace[i];
    const dim = rackDimensionsMap.get(rackNumber);
    if (!dim) continue;

    const rotated = getRotatedDimensions(dim, anchorRotation);
    const size = config.direction === 'horizontal' ? rotated.width : rotated.depth;

    if (i < racksPerRow - 1) {
      rowSpan += size + config.rackSpacing;
    } else {
      rowSpan += size;
    }
  }

  // Calculate double-row depth (including aisle)
  const firstRackDim = rackDimensionsMap.get(racksToPlace[0]);
  if (!firstRackDim) return { width: rowSpan, depth: 0 };

  const rotated = getRotatedDimensions(firstRackDim, anchorRotation);
  const singleRowDepth = config.direction === 'horizontal' ? rotated.depth : rotated.width;
  const doubleRowDepth = singleRowDepth * 2 + config.aisleWidth;

  if (config.direction === 'horizontal') {
    return { width: rowSpan, depth: doubleRowDepth };
  } else {
    return { width: doubleRowDepth, depth: rowSpan };
  }
}

/**
 * Sort unplaced racks by rack number for consistent ordering
 */
export function sortRacksByNumber(summaries: RackSummary[]): RackSummary[] {
  return [...summaries].sort((a, b) => {
    // Try numeric comparison first
    const numA = parseInt(a.rackNumber, 10);
    const numB = parseInt(b.rackNumber, 10);

    if (!isNaN(numA) && !isNaN(numB)) {
      return numA - numB;
    }

    // Fall back to string comparison
    return a.rackNumber.localeCompare(b.rackNumber);
  });
}
